import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

export default function SiteNav() {
    const [open, setOpen] = useState(false);
    const loc = useLocation();
    const onLanding = loc.pathname === "/";

    const links = [
        { href: "#how", label: "How it works" },
        { href: "#programs", label: "Programs" },
        { href: "#stories", label: "Stories" },
        { href: "#faq", label: "FAQ" },
    ];

    return (
        <header className="sticky top-0 z-40 w-full backdrop-blur-md bg-background/80 border-b border-border/60">
            <div className="mx-auto max-w-7xl px-5 sm:px-8 min-h-16 py-2 flex items-center justify-between gap-4">
                {/* Brand stack: Owed logo on top, "What it does" pill button beneath. */}
                <div className="flex flex-col items-start gap-1.5 shrink-0">
                    <Link to="/" className="flex items-center gap-2 group" data-testid="sitenav-brand">
                        <LogoMark />
                        <span className="font-serif text-xl tracking-tight text-foreground">Owed</span>
                        <span className="hidden sm:inline text-[10px] uppercase tracking-[0.18em] text-muted-foreground border border-border rounded-full px-1.5 py-0.5 ml-1">Beta</span>
                    </Link>
                    <Link
                        to="/capabilities"
                        className="ml-7 inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-foreground/5 hover:bg-foreground hover:text-background border border-border text-[11px] font-mono uppercase tracking-[0.12em] text-foreground/80 transition-colors"
                        data-testid="sitenav-capabilities-link"
                    >
                        What it does <ArrowUpRight className="h-3 w-3" />
                    </Link>
                </div>

                {onLanding && (
                    <nav className="hidden lg:flex items-center gap-6 flex-1 justify-center">
                        {links.map((l) => (
                            <a key={l.href} href={l.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors link-underline whitespace-nowrap">
                                {l.label}
                            </a>
                        ))}
                    </nav>
                )}

                <div className="hidden md:flex items-center gap-3 shrink-0">
                    <Link to="/auth" className="text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">
                        Sign in
                    </Link>
                    <Button asChild className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-9 px-5 whitespace-nowrap">
                        <Link to="/onboarding">Start for free</Link>
                    </Button>
                </div>

                <button className="md:hidden p-2 -mr-2" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu">
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            <div className={cn("md:hidden overflow-hidden border-t border-border/60 transition-[max-height] duration-300", open ? "max-h-96" : "max-h-0") }>
                <div className="px-5 py-4 flex flex-col gap-4 bg-background">
                    {onLanding && links.map((l) => (
                        <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-sm text-foreground">
                            {l.label}
                        </a>
                    ))}
                    <div className="pt-3 border-t border-border flex flex-col gap-2">
                        <Button asChild variant="ghost" className="justify-start"><Link to="/capabilities" onClick={() => setOpen(false)}>What it does</Link></Button>
                        <Button asChild variant="ghost" className="justify-start"><Link to="/auth" onClick={() => setOpen(false)}>Sign in</Link></Button>
                        <Button asChild className="rounded-full bg-foreground text-background"><Link to="/onboarding" onClick={() => setOpen(false)}>Start for free</Link></Button>
                    </div>
                </div>
            </div>
        </header>
    );
}

function LogoMark() {
    return (
        <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-soft">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3v18" />
                <path d="M17 7H9.5a3.5 3.5 0 0 0 0 7H14a3.5 3.5 0 0 1 0 7H6" />
            </svg>
            <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-accent animate-pulse" />
        </span>
    );
}
